import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Icon } from "../lib/Icon";
import { BRANDS, BRAND_ORDER, brandVars, type BrandId } from "./brands";
import { BrandMark } from "./BrandMark";
import { usePortal } from "./state";
import { completeMfa, fetchNetworks, signInPortal } from "./portalSession";
import { PORTAL_AUTH_METHODS } from "@recursive-staff/schema";

type Method = (typeof PORTAL_AUTH_METHODS)[number];

// RealtyID first (DECISIONS.md §2 — permanent credential, trains the durable
// habit), then passkey, then the brand account, then single sign-on.
const TABS: { key: Method; label: (brandName: string) => string }[] = [
  { key: "rid", label: () => "RealtyID" },
  { key: "passkey", label: () => "Passkey" },
  { key: "pw", label: (n) => `${n} account` },
  { key: "sso", label: () => "Single sign-on" },
];

export function SignIn() {
  const { brand, brandId, setBrand, refresh } = usePortal();
  const navigate = useNavigate();
  // The brand-account path (network + email + password, with MFA) is the wired
  // one; RealtyID / passkey / SSO are identity routes whose Displet exchange lands
  // in a follow-up, so they're shown but marked not-yet-enabled.
  const [method, setMethod] = useState<Method>("pw");
  const [networkUid, setNetworkUid] = useState("");
  const [networks, setNetworks] = useState<{ uid: string; name: string }[]>([]);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [org, setOrg] = useState("");
  const [rid, setRid] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState("");

  // Populate the network picker (real, from the Displet roster). When the portal
  // auth isn't configured yet the list is empty — the form says so rather than
  // pretending to sign anyone in.
  useEffect(() => {
    let alive = true;
    fetchNetworks().then((r) => {
      if (alive) setNetworks(r.networks);
    });
    return () => {
      alive = false;
    };
  }, []);

  // A sign-out landing here resets to the wired path and clears transient state.
  useEffect(() => {
    setMethod("pw");
    setError(null);
    setMfaToken(null);
  }, [brandId]);

  const canSubmit =
    method === "rid" ? rid.trim().length > 0
    : method === "passkey" ? email.trim().length > 0
    : method === "pw" ? networkUid.length > 0 && email.trim().length > 0 && pw.length > 0
    : org.trim().length > 0;

  async function submit(e?: React.FormEvent) {
    e?.preventDefault();
    setError(null);
    if (method !== "pw") {
      setError(`This sign-in method isn't enabled yet — use your ${brand.name} account for now.`);
      return;
    }
    if (!canSubmit || busy) return;
    setBusy(true);
    const r = await signInPortal(networkUid, email.trim(), pw, remember);
    setBusy(false);
    if (r.ok && "mfaRequired" in r) {
      setMfaToken(r.mfaToken); // step up to the code screen
      return;
    }
    if (r.ok) {
      await refresh();
      navigate({ to: "/portal/home" });
      return;
    }
    setError(
      r.notConfigured
        ? "Portal sign-in isn't enabled here yet. Please check back soon."
        : "That didn't work — check the association, email and password.",
    );
  }

  async function submitMfa(e?: React.FormEvent) {
    e?.preventDefault();
    setError(null);
    if (!code.trim() || busy || !mfaToken) return;
    setBusy(true);
    const r = await completeMfa(mfaToken, code.trim(), remember);
    setBusy(false);
    if (r.ok && !("mfaRequired" in r)) {
      await refresh();
      navigate({ to: "/portal/home" });
      return;
    }
    setError("That code didn't match. Try again.");
  }

  const on = "#fff";
  const hasOrg = org.trim().length >= 3;

  return (
    <div
      style={{
        ...brandVars(brand),
        height: "100vh",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 400px), 1fr))",
        overflowY: "auto",
        fontFamily: "'Space Grotesk', sans-serif",
      }}
    >
      {/* LEFT — brand surface */}
      <div style={{ background: "var(--sf)", color: "var(--on)", padding: "56px 52px", display: "flex", flexDirection: "column", minHeight: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
          <BrandMark brand={brand} on={on} variant="signin" />
          <span style={{ fontWeight: 500, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "var(--on3)", alignSelf: "center" }}>Help</span>
        </div>
        <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--on3)", marginTop: 8 }}>{brand.tag}</div>

        <h1 style={{ margin: "64px 0 0", fontSize: 38, lineHeight: 1.15, fontWeight: 600, letterSpacing: "-1.4px", maxWidth: "15ch", textWrap: "pretty" }}>
          Documentation for the people who run it.
        </h1>
        <p style={{ margin: "18px 0 0", fontSize: 15.5, lineHeight: 1.65, color: "var(--on2)", maxWidth: "44ch", textWrap: "pretty" }}>
          {brand.blurb} Every article is scoped to your role and to what your organization actually licenses, so what you read applies to you.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 11, marginTop: 32 }}>
          {[
            ["lock", "Signed-in only. Nothing here is on the public web."],
            ["building-2", "Platform docs, plus a private space for each organization you belong to."],
            ["ticket", "Your open tickets travel with you — ask from any article."],
          ].map(([icon, text]) => (
            <div key={text} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, lineHeight: 1.5, color: "var(--on2)" }}>
              <Icon name={icon!} size={15} color="var(--on3)" />
              <span>{text}</span>
            </div>
          ))}
        </div>

        <div style={{ flex: 1, minHeight: 32 }} />
        <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--on3)", marginBottom: 11 }}>Other product help centers</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {BRAND_ORDER.map((k) => {
            const active = k === brandId;
            return (
              <button
                key={k}
                onClick={() => setBrand(k as BrandId)}
                style={{
                  border: "1px solid var(--onBd)",
                  background: active ? "rgba(255,255,255,.22)" : "transparent",
                  color: "var(--on)",
                  borderRadius: 3,
                  padding: "8px 13px",
                  fontSize: 12.5,
                  fontWeight: active ? 700 : 500,
                  cursor: "pointer",
                }}
              >
                {BRANDS[k].name}
              </button>
            );
          })}
        </div>
      </div>

      {/* RIGHT — credential panel */}
      <div style={{ background: "#fff", padding: "56px 52px", display: "flex", flexDirection: "column", minHeight: "100%" }}>
        <div style={{ width: "100%", maxWidth: 400, margin: "auto" }}>
          <h2 style={{ margin: 0, fontSize: 25, fontWeight: 600, letterSpacing: "-0.8px", color: "#1d241f" }}>Sign in to continue</h2>
          <p style={{ margin: "8px 0 0", fontSize: 14, lineHeight: 1.6, color: "#8a8e88" }}>
            Use the same credentials you use for {brand.name} itself, or your RealtyID. We do not keep a separate help-center password.
          </p>

          <div style={{ display: "flex", gap: 0, marginTop: 26, borderBottom: "1px solid #ececec" }}>
            {TABS.map((t) => {
              const active = method === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setMethod(t.key)}
                  style={{
                    border: 0,
                    background: "transparent",
                    padding: "9px 2px",
                    marginRight: 22,
                    fontSize: 13,
                    fontWeight: 600,
                    color: active ? "var(--bi)" : "#8a8e88",
                    borderBottom: `2px solid ${active ? "var(--bi)" : "transparent"}`,
                    marginBottom: -1,
                    cursor: "pointer",
                  }}
                >
                  {t.label(brand.name)}
                </button>
              );
            })}
          </div>

          {error && <div style={{ marginTop: 18 }}><ErrorBanner>{error}</ErrorBanner></div>}

          {method === "rid" && (
            <Panel onSubmit={submit}>
              <Note icon="badge-check">
                Your <strong style={{ fontWeight: 600, color: "#1d241f" }}>RealtyID</strong> is permanent and follows you between brokerages and boards. Sign in with it and your access follows your current licence and roles.
              </Note>
              <Field label="RealtyID or licence number">
                <input value={rid} onChange={(e) => setRid(e.target.value)} placeholder="RID-4417-8830 or NC-284117" autoComplete="username" style={{ ...inputStyle, fontFamily: "'Space Mono', monospace" }} />
              </Field>
              {/* No client-side "found" card: it would echo a record from a length
                  check, an enumeration-shaped oracle. The real flow confirms the
                  identity after the one-time code. */}
              <Primary disabled={!canSubmit}>Continue with RealtyID</Primary>
              <Hint>We will send a one-time code to the contact details on your RealtyID record.</Hint>
            </Panel>
          )}

          {method === "passkey" && (
            <Panel onSubmit={submit}>
              <Note icon="fingerprint">
                No password to type or forget. Your device confirms it is you with Face ID, Touch ID, Windows Hello or a security key.
              </Note>
              <Field label="Email">
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourmls.org" type="email" autoComplete="username webauthn" style={inputStyle} />
              </Field>
              <Primary disabled={!canSubmit} icon="fingerprint">Sign in with a passkey</Primary>
              <div style={{ display: "flex", gap: 9, alignItems: "flex-start", paddingTop: 2 }}>
                <Icon name="monitor-smartphone" size={14} color="#8a8e88" />
                <div style={{ fontSize: 12, lineHeight: 1.55, color: "#8a8e88", textWrap: "pretty" }}>
                  On a device without your passkey, choose <strong style={{ fontWeight: 600, color: "#4a504a" }}>use a passkey from another device</strong> and scan the code with your phone.
                </div>
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.55, color: "#8a8e88", textAlign: "center", paddingTop: 8, borderTop: "1px solid #f2f2f2" }}>
                No passkey yet? Sign in another way, then add one from your profile — it takes about ten seconds.
              </div>
            </Panel>
          )}

          {method === "pw" && !mfaToken && (
            <Panel onSubmit={submit}>
              <Field label="Association">
                <select value={networkUid} onChange={(e) => setNetworkUid(e.target.value)} style={{ ...inputStyle, appearance: "auto" }}>
                  <option value="">{networks.length ? "Choose your association…" : "Sign-in isn't enabled yet"}</option>
                  {networks.map((n) => (
                    <option key={n.uid} value={n.uid}>{n.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Email">
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourmls.org" type="email" autoComplete="username" style={inputStyle} />
              </Field>
              <Field label="Password">
                <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••••••" autoComplete="current-password" style={inputStyle} />
              </Field>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#4a504a", cursor: "pointer" }}>
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} style={{ accentColor: "var(--bi)" }} />
                  Keep me signed in for 30 days
                </label>
                <a href="#" onClick={(e) => e.preventDefault()} style={{ fontSize: 12.5, color: "var(--bi)" }}>Forgot password</a>
              </div>
              <Primary disabled={!canSubmit || busy}>{busy ? "Signing in…" : "Sign in"}</Primary>
              <Hint>The same account you use to sign in to {brand.name}. Two-factor is required for board admin and broker admin roles.</Hint>
            </Panel>
          )}

          {method === "pw" && mfaToken && (
            <Panel onSubmit={submitMfa}>
              <Note icon="shield-check">Enter the verification code from your authenticator or the message we just sent.</Note>
              <Field label="Verification code">
                <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} inputMode="numeric" autoComplete="one-time-code" placeholder="123456" style={{ ...inputStyle, fontFamily: "'Space Mono', monospace", letterSpacing: 3 }} />
              </Field>
              <Primary disabled={!code.trim() || busy}>{busy ? "Verifying…" : "Verify and sign in"}</Primary>
              <button type="button" onClick={() => { setMfaToken(null); setCode(""); setError(null); }} style={{ border: 0, background: "transparent", color: "#8a8e88", fontSize: 12.5, cursor: "pointer" }}>Start over</button>
            </Panel>
          )}

          {method === "sso" && (
            <Panel onSubmit={submit}>
              <Field label="Organization">
                <input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Coastal Carolinas MLS" autoComplete="organization" style={inputStyle} />
              </Field>
              {hasOrg && (
                // Echoes only what the user typed — no external record lookup.
                <div style={{ background: "#fafaf7", borderRadius: 3, boxShadow: "inset 0 0 0 1px #ececec", padding: "13px 14px", display: "flex", alignItems: "center", gap: 11 }}>
                  <Icon name="key-round" size={16} color="var(--bi)" />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{org}</div>
                    <div style={{ fontSize: 12, color: "#8a8e88", marginTop: 2, overflowWrap: "anywhere" }}>You'll continue to your organization's sign-in.</div>
                  </div>
                </div>
              )}
              <Primary disabled={!canSubmit} icon="external-link">Continue to single sign-on</Primary>
              <Hint>You will return here once your organization signs you in. Your roles come with you.</Hint>
            </Panel>
          )}

          <div style={{ marginTop: 34, paddingTop: 20, borderTop: "1px solid #f2f2f2", fontSize: 12.5, lineHeight: 1.6, color: "#8a8e88" }}>
            No account yet? Your MLS or brokerage administrator provisions access — <a href="#" onClick={(e) => e.preventDefault()} style={{ color: "var(--bi)" }}>find your administrator</a>. For a licensing or billing question you can <a href="#" onClick={(e) => e.preventDefault()} style={{ color: "var(--bi)" }}>reach us without signing in</a>.
          </div>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #dcdad2",
  borderRadius: 2,
  fontSize: 14,
  color: "#1d241f",
  outline: 0,
  fontFamily: "inherit",
};

function Panel({ onSubmit, children }: { onSubmit: (e: React.FormEvent) => void; children: React.ReactNode }) {
  // A real <form> so Enter submits and password managers engage on the fields.
  return (
    <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 15, marginTop: 24 }}>
      {children}
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#8a8e88", marginBottom: 6 }}>{label}</span>
      {children}
    </label>
  );
}

function Note({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "var(--bs)", borderRadius: 3, boxShadow: "inset 0 0 0 1px var(--bl)", padding: "12px 14px" }}>
      <Icon name={icon} size={15} color="var(--bi)" />
      <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "#4a504a" }}>{children}</div>
    </div>
  );
}

function Primary({ icon, children, disabled }: { icon?: string; children: React.ReactNode; disabled?: boolean }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      style={{
        border: 0,
        // Disabled reads as a real idle state, not white-on-grey (COMPONENTS.md).
        background: disabled ? "#f0efe9" : "var(--bi)",
        color: disabled ? "#8a8e88" : "#fff",
        borderRadius: 3,
        padding: "12px 16px",
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 1.2,
        textTransform: "uppercase",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {icon && <Icon name={icon} size={15} color={disabled ? "#8a8e88" : "#fff"} />}
      {children}
    </button>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, lineHeight: 1.55, color: "#8a8e88", textAlign: "center" }}>{children}</div>;
}

function ErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <div role="alert" style={{ display: "flex", gap: 9, alignItems: "flex-start", background: "#fbe9e7", borderRadius: 3, boxShadow: "inset 0 0 0 1px #f3c9c2", padding: "11px 13px" }}>
      <Icon name="triangle-alert" size={15} color="#b3261e" />
      <div style={{ fontSize: 12.5, lineHeight: 1.5, color: "#8a2f22" }}>{children}</div>
    </div>
  );
}
